<script>
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation.js";
import useSharedMethod from "@/use/SharedMethod";
import {useContratStore} from "@/stores/Contrat";
import useSharedSelectData from "@/use/SharedSelectData";
import WCodeGeneration from "@/components/WCodeGeneration.vue";
import axios from "axios";
import StrQuillEditorCorePdf from "@/components/StrQuillEditorCorePdf.vue";

export default {
    components: {StrQuillEditorCorePdf, WCodeGeneration},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        renewAction: Boolean,
        archiveAction: Boolean,
        unarchiveAction: Boolean,
        activateAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.deleteAction || props.viewAction || props.archiveAction || props.unarchiveAction || props.activateAction);
        const {dialog, loading, overlay, overlayOpacity} = useSharedData();
        const {contratVariables} = useSharedSelectData();
        const {$route, $refs, $moment} = useTools();
        const {mxRules} = useValidation();
        const {getBack} = useSharedMethod();
        const contratStore = useContratStore();

        const contrat = ref({
            status: "ACTIVE",
            personalizedVariables: [],
            originTemplateText: null,
            appliedTemplateText: null,
            templateText: null,
            templateTextEdited: false,
        });

        const stepper = ref(1);

        const oldContrat = ref({});
        const codeLoad = ref(false);

        function findById(id) {
            loading.value = true;
            axios.get(`/contrats/${id}`).then(response => {
                if (props.renewAction) {
                    oldContrat.value = response.data;

                    contrat.value.workerId = oldContrat.value.workerId;
                    contrat.value.contratTemplateId = oldContrat.value.contratTemplateId;
                    contrat.value.natureId = oldContrat.value.natureId;

                    if (oldContrat.value.end) {
                        contrat.value.start = $moment(oldContrat.value.end)
                            .add(1, "days")
                            .format("YYYY-MM-DD");
                    }

                    completeContratData();
                    codeLoad.value = true;
                } else {
                    contrat.value = response.data;
                }
            }).finally(() => {
                loading.value = false;
            })
        }

        function completeContratData() {
            if (contrat.value.start && contrat.value.contratTemplateId) {
                axios.get(`/contrats/completeContratData`, {
                    params: {
                        renewAction: props.renewAction,
                        start: contrat.value.start,
                        contratTemplateId: contrat.value.contratTemplateId,
                    },
                }).then((response) => {
                    const result = response.data;

                    contrat.value = {
                        ...contrat.value,
                        determinedTime: result.determinedTime,
                        duration: result.duration,
                        periodicity: result.periodicity,
                        trialPeriod: result.trialPeriod,
                        trialPeriodStatus: result.trialPeriodStatus,
                        trialPeriodDuration: result.trialPeriodDuration,
                        trialPeriodPeriodicity: result.trialPeriodPeriodicity,
                        trialPeriodEnd: result.trialPeriodEnd,
                        renewedTrialPeriod: result.renewedTrialPeriod,
                        end: result.end
                    }
                });
            }
        }

        function getPersonalizedVariables() {
            if (contrat.value.contratTemplateId) {
                axios.get('/contrats/getPersonalizedVariables', {
                    params: {
                        contratTemplateId: contrat.value.contratTemplateId,
                    }
                }).then(response => {
                    contrat.value.personalizedVariables = response.data;
                });
            }
        }

        function templateChange() {
            completeContratData();
            getPersonalizedVariables();
        }

        function startChange() {
            completeContratData();
        }

        function getImplementedModel() {
            loading.value = true;
            axios.put(`/contrats/getImplementedModel`, contrat.value).then(response => {
                contrat.value.templateText = response.data;
                templateTextOptionDialog.value = false;
                stepper.value = 2;
            }).finally(() => {
                loading.value = false;
            });
        }

        const firstStepIsValid = ref(true);

        function next() {
            firstStepIsValid.value = $refs.form1.validate();
            if (firstStepIsValid.value) {
                templateChanges.value = null;
                templateTextOption.value = null;
                checkTemplateChange();
            }
        }

        const templateName = ref(null);
        const equality = ref(false);
        let currentTemplate = null;
        let originTemplate = null;

        function checkTemplateChange() {
            loading.value = true;
            axios.get(`/contrats/checkTemplateChange`, {
                params: {
                    id: $route.params.id,
                    templateId: contrat.value.contratTemplateId,
                    newItem: props.addAction,
                }
            }).then(response => {
                templateName.value = response.data.templateName;
                currentTemplate = response.data.currentTemplate;
                originTemplate = response.data.originTemplate;
                equality.value = response.data.equality;

                contrat.value.originTemplateText = originTemplate;

                if (props.addAction || (equality.value && !contrat.value.templateTextEdited)) {
                    contrat.value.templateText = currentTemplate;
                    getImplementedModel();
                } else {
                    templateTextOptionDialog.value = true;
                }

            }).finally(() => {
                loading.value = false;
            });
        }

        const templateTextOptionDialog = ref(false);
        const templateTextOption = ref(null);

        function selectTemplateTextOption() {
            if ($refs.templateTextOptionForm.validate()) {
                switch (templateTextOption.value) {
                    case 'RENEW_MODEL':
                        contrat.value.templateText = currentTemplate;
                        contrat.value.originTemplateText = currentTemplate;
                        break;
                    case 'KEEP_MODEL':
                        contrat.value.templateText = originTemplate;
                        contrat.value.originTemplateText = originTemplate;
                        break;
                    case 'SKIP_MODEL':
                        contrat.value.templateText = contrat.value.appliedTemplateText;
                        contrat.value.originTemplateText = originTemplate;
                        break;
                }
                getImplementedModel();
            }
        }

        const templateChanges = ref(null);

        function explainTemplateChanges() {
            loading.value = true;
            axios.get(`/contrats/explainTemplateChanges`, {
                params: {
                    id: $route.params.id,
                    templateId: contrat.value.contratTemplateId,
                }
            }).then(response => {
                templateChanges.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        const checkIntegrityResult = ref(null);
        const checkIntegrityDialog = ref(false);

        function checkIntegrity() {
            loading.value = true;
            axios.post(`/contrats/checkIntegrity`, contrat.value)
                .then(response => {
                    checkIntegrityResult.value = response.data;
                    if (checkIntegrityResult.value === 'CLEAN') {
                        save();
                    } else {
                        checkIntegrityDialog.value = true;
                    }
                }).finally(() => {
                loading.value = false;
            })
        }

        function save() {
            loading.value = true;
            axios.post('/contrats', contrat.value).then((response) => {
                if (checkIntegrityResult.value === 'ARCHIVE') {
                    contratStore.removeArchiveByWorkerId(contrat.value.workerId);
                }
                contratStore.addContrat(response.data);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        function update() {
            loading.value = true;
            axios.put('/contrats', contrat.value).then((response) => {
                if (contrat.value.status === "ARCHIVE") {
                    contratStore.deleteContrat(response.data);
                } else {
                    contratStore.updateContrat(response.data);
                }
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        function archive() {
            contrat.value.status = "ARCHIVE";
            update();
        }

        function unarchive() {
            contrat.value.status = "ACTIVE";
            update();
        }

        function activate() {
            contrat.value.status = "ACTIVE";
            contrat.value.trialPeriodStatus = "ACTIVE";
            update();
        }

        function remove() {
            loading.value = true;
            axios.delete(`/contrats/${contrat.value.id}`).then(() => {
                contratStore.deleteContrat(contrat.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            codeLoad,
            templateTextOptionDialog,
            templateTextOption,
            templateName,
            equality,
            mxRules,
            readonly,
            dialog,
            overlayOpacity,
            loading,
            overlay,
            contrat,
            oldContrat,
            stepper,
            firstStepIsValid,
            checkIntegrityDialog,
            checkIntegrityResult,
            contratVariables,
            selectTemplateTextOption,
            explainTemplateChanges,
            next,
            getBack,
            templateChange,
            startChange,
            checkIntegrity,
            save,
            update,
            archive,
            unarchive,
            activate,
            remove,
            templateChanges
        }
    }
}
</script>

<template>
    <div>
        <v-dialog
            transition="dialog-bottom-transition"
            v-model="dialog"
            persistent
            max-width="1000"
            scrollable
        >
            <v-card>
                <w-dialog-toolbar
                    :add-action="addAction"
                    :update-action="updateAction"
                    :delete-action="deleteAction"
                    :view-action="viewAction"
                    :renew-action="renewAction"
                    :archive-action="archiveAction"
                    :unarchive-action="unarchiveAction"
                    :activate-action="activateAction"
                />
                <w-overlay
                    :overlay="overlay"
                    :loading="loading"
                    :delete-action="deleteAction"
                    :archive-action="archiveAction"
                    :unarchive-action="unarchiveAction"
                    :activate-action="activateAction"
                    @remove="remove"
                    @archive="archive"
                    @unarchive="unarchive"
                    @activate="activate"
                    @cancel="overlay = false"
                />
                <v-card-text>
                    <v-container fluid>
                        <template>
                            <v-stepper v-model="stepper" color="blue" non-linear>
                                <v-stepper-header>
                                    <v-stepper-step
                                        :step="1"
                                        :rules="[() => firstStepIsValid]"
                                        :editable="true"
                                    >
                                        {{ $t('common.general') }}
                                    </v-stepper-step>

                                    <v-divider></v-divider>

                                    <v-stepper-step
                                        :step="2"
                                        :editable="true"
                                    >
                                        {{ $t('common.modal') }}
                                    </v-stepper-step>


                                </v-stepper-header>

                                <v-stepper-items>
                                    <v-stepper-content step="1">
                                        <v-form lazy-validation ref="form1">
                                            <w-fieldset :title="$t('common.details')">
                                                <v-row dense>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('contrat.number')" required/>
                                                        <w-code-generation
                                                            v-model="contrat.number"
                                                            :entity="'CONTRAT'"
                                                            :load="addAction || (renewAction && codeLoad)"
                                                            :readonly="!addAction && !renewAction && !updateAction"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('module.worker')" required/>
                                                        <str-worker-autocomplete
                                                            v-model="contrat.workerId"
                                                            action="SAVE_CONTRAT"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('common.modal')" required/>
                                                        <str-contrat-template-autocomplete
                                                            v-model="contrat.contratTemplateId"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                            @change="templateChange"
                                                        />
                                                    </v-col>

                                                    <v-col cols="6">
                                                        <w-label :label="$t('data_select.CONTRAT_NATURE')" required/>
                                                        <w-data-select-autocomplete
                                                            v-model="contrat.natureId"
                                                            type="CONTRAT_NATURE"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col cols="4">
                                                        <w-label :label="$t('contrat.date')" required/>
                                                        <w-date-picker
                                                            v-model="contrat.date"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col cols="4">
                                                        <w-label :label="$t('common.start')" required/>
                                                        <w-date-picker
                                                            v-model="contrat.start"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                            @change="startChange"
                                                        />
                                                    </v-col>
                                                    <v-col cols="4">
                                                        <w-label :label="$t('common.end')"/>
                                                        <w-date-picker
                                                            v-model="contrat.end"
                                                            :readonly="readonly"
                                                        />
                                                    </v-col>
                                                    <v-col cols="12">
                                                        <w-label :label="$t('common.description')"/>
                                                        <w-simple-text-area
                                                            v-model="contrat.description"
                                                            :readonly="readonly"
                                                        />
                                                    </v-col>
                                                </v-row>
                                            </w-fieldset>
                                            <br/>
                                            <w-fieldset
                                                v-if="contrat.personalizedVariables.length > 0"
                                                :title="$t('module.personalized_variable')"
                                            >
                                                <v-row dense>
                                                    <v-col
                                                        v-for="(item, index) in contrat.personalizedVariables"
                                                        :key="`pv-${index}`"
                                                        cols="6"
                                                    >
                                                        <w-label
                                                            :label="item.personalizedVariableActivateNamedValue ? item.personalizedVariableNamedValue : item.personalizedVariableName"
                                                            :required="item.personalizedVariableRequired"
                                                        />
                                                        <w-text-field
                                                            v-if="['PRICE', 'NUMBER'].includes(item.personalizedVariableType)"
                                                            v-model="item.value"
                                                            type="number"
                                                            :rules="item.personalizedVariableRequired ? [mxRules.required] : []"
                                                        />
                                                        <w-text-field
                                                            v-else-if="item.personalizedVariableType === 'TEXT'"
                                                            v-model="item.value"
                                                            :rules="item.personalizedVariableRequired ? [mxRules.required] : []"
                                                        />
                                                        <w-date-picker
                                                            v-else-if="item.personalizedVariableType === 'DATE'"
                                                            v-model="item.value"
                                                            :rules="item.personalizedVariableRequired ? [mxRules.required] : []"
                                                        />
                                                        <w-month-picker
                                                            v-else-if="item.personalizedVariableType === 'MONTH'"
                                                            v-model="item.value"
                                                            :rules="item.personalizedVariableRequired ? [mxRules.required] : []"
                                                        />
                                                        <w-time-picker
                                                            v-else-if="item.personalizedVariableType === 'TIME'"
                                                            v-model="item.value"
                                                            :rules="item.personalizedVariableRequired ? [mxRules.required] : []"
                                                        />
                                                    </v-col>
                                                </v-row>
                                            </w-fieldset>
                                            <br/>
                                            <w-fieldset
                                                v-if="contrat.trialPeriod"
                                                title="Période d'essai"
                                            >
                                                <v-row dense>
                                                    <v-col cols="12">
                                                        <h3>
                                                            Période d'essai
                                                            <strong class="red--text">
                                                                {{ contrat.trialPeriodDuration }}
                                                                {{ $t(`common.${contrat.trialPeriodPeriodicity}`) }}
                                                            </strong>
                                                            <span
                                                                class="teal--text"
                                                                v-if="contrat.renewedTrialPeriod"
                                                            >
                                                                , {{ $t('contrat.renewed_trial_period') }}
                                                            </span>
                                                        </h3>
                                                    </v-col>
                                                </v-row>
                                                <v-row dense>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('common.start')" required/>
                                                        <w-date-picker
                                                            v-model="contrat.start"
                                                            readonly
                                                            :rules="[mxRules.required]"
                                                        />
                                                    </v-col>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('common.end')" required/>
                                                        <w-date-picker
                                                            v-model="contrat.trialPeriodEnd"
                                                            :readonly="readonly"
                                                            :rules="[mxRules.required]"
                                                        ></w-date-picker>
                                                    </v-col>

                                                    <v-col
                                                        cols="12"
                                                        v-if="contrat.endRenewedTrialPeriod"
                                                    >
                                                        <w-label :label="$t('contrat.end_renewed_trial_period')"/>
                                                        <w-date-picker
                                                            v-model="contrat.endRenewedTrialPeriod"
                                                            readonly
                                                        />
                                                    </v-col>
                                                </v-row>
                                            </w-fieldset>
                                            <br/>
                                            <w-fieldset
                                                v-if="contrat.leavingDate"
                                                :title="$t('contrat.termination')"
                                            >
                                                <v-row dense>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('data_select.CONTRAT_EXIT_RATIONALE')"/>
                                                        <w-data-select-autocomplete
                                                            v-model="contrat.exitRationaleId"
                                                            type="CONTRAT_EXIT_RATIONALE"
                                                            color="red"
                                                            readonly
                                                        />
                                                    </v-col>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('contrat.terminate_date')"/>
                                                        <w-date-picker
                                                            v-model="contrat.leavingDate"
                                                            readonly
                                                            :color="'red'"
                                                        />
                                                    </v-col>
                                                    <v-col cols="12">
                                                        <w-label :label="$t('common.description')"/>
                                                        <w-text-field
                                                            v-model="contrat.leavingDescription"
                                                            color="red"
                                                            :background-color="'red lighten-5'"
                                                        />
                                                    </v-col>
                                                </v-row>
                                            </w-fieldset>
                                        </v-form>
                                    </v-stepper-content>

                                    <v-stepper-content step="2">
                                        <str-quill-editor
                                            v-model="contrat.templateText"
                                            :disabled="readonly"
                                            :variables="contratVariables"
                                        ></str-quill-editor>

                                        <str-quill-editor-core-pdf v-model="contrat.templateText" hide/>
                                    </v-stepper-content>

                                </v-stepper-items>
                            </v-stepper>
                        </template>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-previous
                        :show="stepper === 2 && (addAction || updateAction || renewAction)"
                        @press="stepper = 1"
                    />
                    <w-btn-next
                        :show="stepper === 1 && (addAction || updateAction || renewAction)"
                        :error="!firstStepIsValid"
                        :loading="loading"
                        @press="next"
                    />
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction && stepper === 2"
                        :loading="loading"
                        @press="checkIntegrity"
                    />
                    <w-btn
                        v-if="renewAction && stepper === 2"
                        icon="mdi-autorenew"
                        :text="$t('common.renew')"
                        :loading="loading"
                        color="cyan"
                        @press="checkIntegrity"
                    />
                    <w-btn-update
                        :show="updateAction && stepper === 2"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-archive
                        :show="archiveAction"
                        @press="overlay = true"
                    />
                    <w-btn-unarchive
                        :show="unarchiveAction"
                        @press="overlay = true"
                    />
                    <w-btn-activate
                        :show="activateAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        @press="overlay = true"
                    />
                    <w-btn-print :show="stepper === 2"/>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            persistent
            scrollable
            max-width="500"
            v-model="checkIntegrityDialog"
        >
            <v-card>
                <v-container fluid>
                    <v-alert
                        v-if="checkIntegrityResult === 'ARCHIVE'"
                        text
                        prominent
                        type="error"
                        icon="mdi-cloud-alert"
                    >
                        {{ $t('contrat.archive_contrat_validation_description') }}
                    </v-alert>
                    <v-alert
                        v-if="checkIntegrityResult === 'PENDING'"
                        text
                        prominent
                        type="orange"
                        icon="mdi-cloud-alert"
                    >
                        {{ $t('contrat.pending_contrat_validation_description') }}
                    </v-alert>
                </v-container>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="checkIntegrityDialog = false"/>
                    <w-btn-add
                        show
                        :loading="loading"
                        @press="save"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            persistent
            scrollable
            max-width="1000"
            v-model="templateTextOptionDialog"
        >
            <v-card>
                <w-toolbar color="blue" :title="templateName"/>
                <v-card-text>
                    <v-form ref="templateTextOptionForm" lazy-validation>
                        <v-container fluid>
                            <v-radio-group
                                v-model="templateTextOption"
                                :rules="[mxRules.required]"
                                row
                            >
                                <v-radio value="RENEW_MODEL">
                                    <template v-slot:label>
                                        <span><strong class="red--text">Appliquer</strong> le nouveau modèle</span>
                                    </template>
                                </v-radio>
                                <v-radio
                                    v-if="!equality"
                                    value="KEEP_MODEL"
                                >
                                    <template v-slot:label>
                                        <div><strong class="teal--text">Garder</strong> le modèle actualle</div>
                                    </template>
                                </v-radio>
                                <v-radio
                                    v-if="contrat.templateTextEdited"
                                    value="SKIP_MODEL"
                                >
                                    <template v-slot:label>
                                        <div><strong class="blue--text">Laisser</strong> tel qu'il est</div>
                                    </template>
                                </v-radio>
                            </v-radio-group>

                            <w-fieldset v-if="templateChanges">
                                <div v-html="templateChanges"></div>
                            </w-fieldset>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="templateTextOptionDialog = false"/>
                    <w-btn
                        color="blue darken-2"
                        icon="mdi-bullseye"
                        :text="$t('common.apply')"
                        :loading="loading"
                        @press="selectTemplateTextOption"
                    />
                    <w-btn
                        color="red"
                        icon="mdi-head-snowflake-outline"
                        :text="$t('common.explain')"
                        :loading="loading"
                        @press="explainTemplateChanges"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>